import React from 'react';
import { cn } from '@/utils/cn';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost' | 'link' | 'outline';
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
  isLoading?: boolean;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  fullWidth?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      loading = false,
      isLoading = false,
      icon,
      iconPosition = 'left',
      fullWidth = false,
      className,
      children,
      disabled,
      ...props
    },
    ref
  ) => {
    const baseStyles = `
      inline-flex items-center justify-center
      font-medium rounded-md
      transition-all duration-200
      focus:outline-none focus:ring-3 focus:ring-offset-2
      disabled:opacity-50 disabled:cursor-not-allowed
      select-none
    `;

    const variantStyles = {
      primary: `
        bg-primary text-primary-foreground
        hover:bg-primary-hover
        active:bg-primary-active
        focus:ring-primary/50
      `,
      secondary: `
        bg-secondary text-secondary-foreground
        hover:bg-secondary-hover
        active:bg-secondary-active
        focus:ring-secondary/50
        border border-border
      `,
      danger: `
        bg-danger text-danger-foreground
        hover:bg-danger-hover
        active:bg-danger-active
        focus:ring-danger/50
      `,
      ghost: `
        bg-transparent text-foreground
        hover:bg-muted
        active:bg-muted-hover
        focus:ring-primary/50
      `,
      link: `
        bg-transparent text-primary
        hover:underline hover:text-primary-hover
        active:text-primary-active
        focus:ring-primary/50
        px-0
      `,
      outline: `
        bg-transparent text-foreground
        border border-border
        hover:bg-muted
        active:bg-muted-hover
        focus:ring-primary/50
      `,
    };

    const sizeStyles = {
      sm: 'h-9 px-3 text-sm gap-1.5',
      md: 'h-11 px-4 text-base gap-2',
      lg: 'h-13 px-6 text-lg gap-2.5',
    };

    const fullWidthStyles = fullWidth ? 'w-full' : '';

    const isLoadingState = loading || isLoading;
    const isDisabled = disabled || isLoadingState;

    return (
      <button
        ref={ref}
        type="button"
        className={cn(
          baseStyles,
          variantStyles[variant],
          sizeStyles[size],
          fullWidthStyles,
          className
        )}
        disabled={isDisabled}
        aria-busy={isLoadingState}
        {...props}
      >
        {isLoadingState && (
          <svg
            className="animate-spin h-4 w-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        )}

        {!isLoadingState && icon && iconPosition === 'left' && (
          <span className="inline-flex shrink-0" aria-hidden="true">
            {icon}
          </span>
        )}

        {children && <span className={isLoadingState ? 'opacity-0' : ''}>{children}</span>}

        {!isLoadingState && icon && iconPosition === 'right' && (
          <span className="inline-flex shrink-0" aria-hidden="true">
            {icon}
          </span>
        )}
      </button>
    );
  }
);

Button.displayName = 'Button';

export default Button;
